import { inject, singleton } from 'tsyringe';
import {
  ExchangeTokenDTOType,
  ExchangeTokenResponseType,
  ExchangeV3Client,
  IExchangeClient
} from '../../clients/shell/exchange';
import IRefreshClient from '../../clients/shell/refresh/IRefreshClient';
import type { SetServiceDependencies } from '../../infra/startup/types';
import { TokenType, UserContextEnum } from '../../interface/types';
import bindAllMethods from '../../utils/bindAllMethods';
import { getCookie } from '../../utils/cookies';
import { AuthTokenService, IAuthTokenService } from '../authTokenService';
import AuthContextEnum from '../authTokenService/AuthContextEnum';
import getParentContext from '../authTokenService/utils/getParentContext';
import { ILocalizationService } from '../localizationService';
import { getUserIdentityProvider } from '../mfeRouterService/operations/NewLoginPathOperation/getUserIdentityProvider';
import { INavigationService } from '../navigationService';
import { SupportSessionService } from '../supportSession';
import ISupportSessionService from '../supportSession/ISupportSessionService';
import { TenantStrategyEnum } from '../tenantHandler/strategy/strategy';
import BaseSessionService from './BaseSessionService';
import ISessionService, {
  LogoutOptionsType,
  RefreshParameterType,
  SignInOptionsType,
  SignInResponseType
} from './ISessionService';
import IdTokenRepository from './IdTokenRepository';
import SessionObserver, { SessionEvents } from './SessionObserver';
import { GenerateAuthenticationUrlParams } from './loginService';
import { ILogoutService } from './logoutService';
import IRefreshTokenService from './refreshTokenService/IRefreshTokenService';
import { clearUserData } from './utils/clearUserData';

export type SessionServiceParameters = {
  refreshClient: IRefreshClient;
  exchangeClient: IExchangeClient;
  supportSessionService: ISupportSessionService;
  authTokenService: IAuthTokenService;
  idTokenRepository: IdTokenRepository;
};

@singleton()
class SessionServiceWeb extends BaseSessionService implements ISessionService {
  private _exchangeClient: IExchangeClient;
  private _supportSessionService: ISupportSessionService;
  private _logoutService: ILogoutService;
  private _sessionService: ISessionService;
  private _navigationService: INavigationService;
  private _localizationService: ILocalizationService;
  private _idTokenRepository: IdTokenRepository;
  private _refreshTokenServiceWeb: IRefreshTokenService;

  constructor(
    @inject(IdTokenRepository) idTokenRepository: IdTokenRepository,
    @inject(AuthTokenService) authTokenService: IAuthTokenService,
    @inject(ExchangeV3Client) exchangeClient: IExchangeClient,
    @inject(SupportSessionService)
    supportSessionService: ISupportSessionService,
    @inject('IRefreshTokenService')
    refreshTokenServiceWeb: IRefreshTokenService
  ) {
    super({ authTokenService, isNative: false });
    this._idTokenRepository = idTokenRepository;
    this._authTokenService = authTokenService;
    this._exchangeClient = exchangeClient;
    this._supportSessionService = supportSessionService;
    this._refreshTokenServiceWeb = refreshTokenServiceWeb;
    bindAllMethods(this);
  }
  signIn(options: SignInOptionsType): Promise<SignInResponseType> {
    throw new Error('Method not implemented.');
  }

  public async forceLogin(): Promise<void> {
    // Web implementation: Redirect to login page
    return new Promise(() => {
      this._loginService.redirectToLogin({ forceLogin: true });
    });
  }

  public setDependencies({ services, ...rest }: SetServiceDependencies): void {
    super.setDependencies({ services, ...rest });
    const {
      logoutService,
      localizationService,
      navigationService,
      sessionService
    } = services;
    this._logoutService = logoutService;
    this._localizationService = localizationService;
    this._navigationService = navigationService;
    this._sessionService = sessionService;

    bindAllMethods(this);
  }

  public async init(): Promise<void> {
    await this._validateSupportSession();
    this._markAsReady();
  }

  public async redirectToIdentityProvider(
    postLoginRedirect?: string
  ): Promise<any> {
    const identityProviderUrl = await getUserIdentityProvider(
      postLoginRedirect,
      {
        localizationService: this._localizationService,
        navigationService: this._navigationService,
        sessionService: this._sessionService
      }
    );

    return new Promise(() => {
      this._navigationService.redirect(identityProviderUrl);
    });
  }

  // TODO: Refactor this to not clearUserData if it's not loggedIn and create a mechanism to deal with that
  // TODO: Refactor to deal with the cookies
  public isLoggedIn(): boolean {
    //TODO: move cookie name to a constant
    const maxAge0OnLoginCookie = getCookie(
      'jarvisreactshelllogin__setMaxAge0OnLogin'
    );

    const isAST = this._supportSessionService.isSupportSession();
    const isLoggedIn =
      (maxAge0OnLoginCookie !== 'true' && this._isSessionActive()) || isAST;

    if (!isLoggedIn) clearUserData();
    return isLoggedIn;
  }

  /** In this case, the user is already logged in.
   */
  public async refresh(
    refreshParameterType?: RefreshParameterType
  ): Promise<void> {
    return this._refreshTokenServiceWeb.refresh(refreshParameterType);
  }

  private _isSessionActive(): boolean {
    return (
      !!getCookie(TokenType.shellSessionActive, false) ||
      !!getCookie(TokenType.shellSessionId, false)
    );
  }

  public getLoginCooldown(): number {
    return 0; // Web version doesn't contain cooldown time.
  }

  /**
   *    * This should be used to ensure that critical scopes is valid. */
  public goForceLogin(): void {
    //return this._criticalScopeHandler.ensureCriticalScopes(waitCallback);
    this._loginService.redirectToLogin({ forceLogin: true });
  }

  public getIdToken(): string {
    return (
      this._idTokenRepository?.findOne()?.token ||
      getCookie(TokenType.stratusIdToken) ||
      getCookie(TokenType.deprecatedstratusIdToken)
    );
  }

  /** In this case, the user is already logged in.
   */
  public async exchangeToken(
    tenantId: string,
    authContext: AuthContextEnum,
    tenantStrategy: TenantStrategyEnum
  ): Promise<void> {
    let exchangeTenantTokenResponse: ExchangeTokenResponseType;
    try {
      const parentContext = getParentContext(authContext);

      const exchangeDTO: ExchangeTokenDTOType = {
        accessToken: this._getAccessToken(parentContext),
        tenantId,
        tenantStrategy
      };

      exchangeTenantTokenResponse = await this._exchangeClient.exchangeToken(
        exchangeDTO
      );
    } catch (err) {
      throw new Error(err);
    }
    await this._saveExchangeData(exchangeTenantTokenResponse);
    await SessionObserver.notify(
      SessionEvents.EXCHANGE_TENANT_TOKEN,
      this._authTokenService.getToken()?.token
    );
  }

  public async clearSession(): Promise<void> {
    clearUserData();
    await this._logoutService.logout();
  }
  public async logout(options?: LogoutOptionsType): Promise<void> {
    return this._logoutService.logout(options || {});
  }

  public async generateAuthenticationUrl(
    options: GenerateAuthenticationUrlParams
  ): Promise<string> {
    return this._loginService.generateAuthenticationUrl(options);
  }

  private _getAccessToken(authContext: AuthContextEnum): string {
    const { token: accessToken } =
      this._authTokenService.getToken(authContext) || {};
    return accessToken;
  }

  private async _saveExchangeData(
    exchangeTenantTokenResponse: ExchangeTokenResponseType
  ): Promise<void> {
    const isTypeCustomer =
      exchangeTenantTokenResponse.tokenType === UserContextEnum.customer;

    this._authTokenService.setToken(
      exchangeTenantTokenResponse.accessToken,
      isTypeCustomer ? AuthContextEnum.subtenant : AuthContextEnum.tenant
    );

    // In OneCloud exchange, a new org-less access token is returned.
    const orgLessAccessToken = exchangeTenantTokenResponse?.orgLessAccessToken;
    if (orgLessAccessToken && orgLessAccessToken !== 'null') {
      this._authTokenService.setToken(
        exchangeTenantTokenResponse.orgLessAccessToken,
        AuthContextEnum.tenantless
      );
    }
  }

  private async _validateSupportSession(): Promise<void> {
    if (
      this._supportSessionService.isSupportSessionStartedBySessionAPIAndInitialStatusNotEnabled()
    ) {
      await this.clearSession();
    }
  }
}

export default SessionServiceWeb;
